
import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CustomerSelector } from './customer-selector';
import { Customer } from './customer-selector/types';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { UserCheck2, AlertCircle, Mail, Phone, MapPin } from "lucide-react";
import OrderFormCustomerStats from './OrderFormCustomerStats';

interface OrderFormCustomerSectionProps {
  orderNumber: string;
  orderDate: string;
  handleOrderNumberChange: (value: string) => void;
  handleOrderDateChange: (value: string) => void;
  handleSelectCustomer: (id: string, name: string) => void;
  selectedCustomerId?: string;
  selectedCustomerName?: string;
  selectedCustomer: Customer | null;
  shopId?: string;
}

const OrderFormCustomerSection = ({
  orderNumber,
  orderDate,
  handleOrderNumberChange,
  handleOrderDateChange,
  handleSelectCustomer,
  selectedCustomerId,
  selectedCustomerName,
  selectedCustomer,
  shopId
}: OrderFormCustomerSectionProps) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h3 className="text-lg font-medium mb-4 dark:text-gray-200">Informations de la commande</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div>
            <Label htmlFor="orderNumber">Numéro de commande</Label>
            <Input 
              id="orderNumber"
              value={orderNumber}
              onChange={(e) => handleOrderNumberChange(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="orderDate">Date de commande</Label>
            <Input 
              id="orderDate"
              type="date"
              value={orderDate}
              onChange={(e) => handleOrderDateChange(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label>Client</Label>
            <div className="mt-1">
              <CustomerSelector 
                onSelectCustomer={handleSelectCustomer}
                selectedCustomerId={selectedCustomerId}
                selectedCustomerName={selectedCustomerName}
                shopId={shopId}
              />
            </div>
            
            {selectedCustomer ? (
              <Alert variant="success" className="mt-3 bg-green-50 border-green-200">
                <UserCheck2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-700">Client sélectionné</AlertTitle>
                <AlertDescription className="text-green-600 space-y-1">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">{selectedCustomer.name}</span>
                  </div>
                  {selectedCustomer.phone && (
                    <div className="flex items-center gap-1 text-sm">
                      <Phone className="h-3 w-3" />
                      <span>{selectedCustomer.phone}</span>
                    </div>
                  )}
                  {selectedCustomer.email && (
                    <div className="flex items-center gap-1 text-sm">
                      <Mail className="h-3 w-3" />
                      <span>{selectedCustomer.email}</span>
                    </div>
                  )}
                  {/* Afficher un résumé de l'emplacement s'il existe, mais l'édition se fera dans l'onglet Localisation */}
                  {(selectedCustomer.region || selectedCustomer.municipality || selectedCustomer.country) && (
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="h-3 w-3" />
                      <span>
                        {[
                          selectedCustomer.municipality,
                          selectedCustomer.region,
                          selectedCustomer.country
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive" className="mt-3 bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-700">Aucun client sélectionné</AlertTitle>
                <AlertDescription className="text-red-600">
                  Veuillez sélectionner un client pour continuer
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
        
        <div className="lg:col-span-1">
          <OrderFormCustomerStats selectedCustomer={selectedCustomer} />
        </div>
      </div>
    </div>
  );
};

export default OrderFormCustomerSection;
